// components/DonutChart.tsx
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useMemo } from "react";
import { PrincipleChartData } from "../../core/_models";

interface Props {
    principleChartData: PrincipleChartData[];
    principleColors: Record<string, string>;
    chartHeight?: number;
}

const DonutChart = ({
    principleChartData,
    principleColors,
    chartHeight = 350,
}: Props) => {
    // Calculate average score for each principle
    const principleAverages = useMemo(() => {
        return principleChartData.map((principle) => {
            const dataSource = principle.chart_data_eval || principle.chart_data_sp || [];

            let totalSum = 0;
            let totalCount = 0;

            dataSource.forEach((item) => {
                if (item.data) {
                    const sum = item.data.reduce((acc, val) => acc + (val || 0), 0);
                    totalSum += sum;
                    totalCount += item.data.length;
                }
            });

            const average = totalCount > 0 ? Number((totalSum / totalCount).toFixed(2)) : 0;

            return {
                name: principle.name,
                average: average,
            };
        });
    }, [principleChartData]);

    // Keep original order (Principle 1, 2, 3, 4) - NO SORTING
    const sortedData = principleAverages;

    const labels = useMemo(() => sortedData.map((item) => item.name), [sortedData]);
    const series = useMemo(() => sortedData.map((item) => item.average), [sortedData]);

    // Map colors to sorted data (in original order)
    const colors = useMemo(() => {
        return sortedData.map((item) => {
            return principleColors[item.name] || '#888888';
        });
    }, [sortedData, principleColors]);

    const options: ApexOptions = useMemo(() => ({
        chart: {
            type: 'donut',
            toolbar: {
                show: true,
                tools: {
                    download: true,
                },
            },
            height: chartHeight,
            width: '100%',
        },
        labels: labels,
        colors: colors,
        legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '12px',
            fontWeight: 600,
            markers: {
                size: 10,
                strokeWidth: 0,
            },
            itemMargin: {
                horizontal: 10,
                vertical: 4,
            },
        },
        tooltip: {
            y: {
                formatter: (val: number) => val.toFixed(2),
                title: {
                    formatter: (seriesName: string) => `${seriesName}`,
                },
            },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '55%',
                    labels: {
                        show: true,

                        // Principle name shown on hover
                        name: {
                            show: true,
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#111827',
                            formatter: (name: string) =>
                                `${name}`,
                        },

                        // Principle score shown on hover
                        value: {
                            show: true,
                            fontSize: '12px',
                            fontWeight: 800,
                            color: '#111827',
                            offsetY: 10,
                            formatter: (val: string) => `Avg. weighted score: \n ${parseFloat(val).toFixed(2)}`
                        },
                    },
                },
            },
        },
        dataLabels: {
            enabled: true,
            style: {
                fontSize: '10px',
                fontWeight: 600,
                colors: ['#fff'],
            },
            formatter: (val: number, { seriesIndex }: { seriesIndex: number }) => {
                const total = series.reduce((sum, v) => sum + v, 0);
                const percentage = total > 0 ? (series[seriesIndex] / total) * 100 : 0;
                return percentage.toFixed(1) + '%';
            },
        },
    }), [labels, colors, series, chartHeight]);

    return (
        <div style={{
            width: '100%',
            height: '100%',
            margin: '0 auto',
        }}>
            <Chart
                options={options}
                series={series}
                type="donut"
                height={chartHeight}
            />
        </div>
    );
};

export default DonutChart;